import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ConstructionCalculator from './ConstructionCalculator';
import RateSheetView from './RateSheetView';
import ForexView from './ForexView';
import ResourcesPlan from './ResourcesPlan';
import RiskAndChange from './RiskAndChange';
import FundingDrawdown from './FundingDrawdown';
import TaxPolicies from './TaxPolicies';
import KpiDashboard from './KpiDashboard';
import EvmAnalysis from './EvmAnalysis';
import Dashboard from './Dashboard';
import Collaboration from './Collaboration';
import BaselineActuals from './BaselineActuals';
import HowToGuide from './HowToGuide';
import PrintButton from './PrintButton';
import MarketRibbon from './MarketRibbon';
import { Button } from '../components/ui/button';
import { useTheme } from './ThemeContext';
import {
  Calculator as CalcIcon, Package, Coins, Users, ShieldAlert, Wallet,
  Landmark, Gauge, TrendingUp, LayoutDashboard, MessageSquare, GitCompare, Sun, Moon,
} from 'lucide-react';

export default function ConstructionShell() {
  const [tab, setTab] = useState('calc');
  const [calculation, setCalculation] = useState(null);
  const [riskEval, setRiskEval] = useState(null);
  const [savedProjectId, setSavedProjectId] = useState(null);
  const [revisedBurn, setRevisedBurn] = useState(null);
  const { theme, toggle } = useTheme();

  const riskAdjustedCost = calculation
    ? (calculation.final_cost_usd || 0) + (riskEval?.expected_risk_cost_usd || 0) + (riskEval?.approved_change_cost_usd || 0)
    : 0;

  const handleCalculated = (res) => {
    setCalculation(res);
    setRiskEval(null);
    setSavedProjectId(null);
  };

  const region = calculation?.region || 'us';

  return (
    <div className="space-y-3" data-testid="construction-shell">
      {/* Market rates ribbon (visible on every tab) */}
      <MarketRibbon region={region} />

      {/* Header actions */}
      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <div /> {/* spacer */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={toggle} data-testid="theme-toggle-btn" title="Toggle theme">
            {theme === 'dark' ? <><Sun className="h-3.5 w-3.5 mr-1.5" />Light</> : <><Moon className="h-3.5 w-3.5 mr-1.5" />Dark</>}
          </Button>
          <HowToGuide />
          <PrintButton testid="shell-print-btn" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 w-full h-auto justify-start no-print">
          <TabsTrigger value="calc" data-testid="const-shell-calc"><CalcIcon className="h-4 w-4 mr-1.5" />Calculator</TabsTrigger>
          <TabsTrigger value="resources" data-testid="const-shell-resources" disabled={!calculation}><Users className="h-4 w-4 mr-1.5" />Resources</TabsTrigger>
          <TabsTrigger value="risk" data-testid="const-shell-risk" disabled={!calculation}><ShieldAlert className="h-4 w-4 mr-1.5" />Risk &amp; Change</TabsTrigger>
          <TabsTrigger value="funding" data-testid="const-shell-funding" disabled={!calculation}><Wallet className="h-4 w-4 mr-1.5" />Funding</TabsTrigger>
          <TabsTrigger value="evm" data-testid="const-shell-evm" disabled={!calculation}><TrendingUp className="h-4 w-4 mr-1.5" />EVM</TabsTrigger>
          <TabsTrigger value="kpi" data-testid="const-shell-kpi" disabled={!calculation}><Gauge className="h-4 w-4 mr-1.5" />KPIs</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="const-shell-dashboard" disabled={!calculation}><LayoutDashboard className="h-4 w-4 mr-1.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="bla" data-testid="const-shell-bla" disabled={!calculation}><GitCompare className="h-4 w-4 mr-1.5" />Baseline &amp; Actuals</TabsTrigger>
          <TabsTrigger value="collab" data-testid="const-shell-collab" disabled={!calculation}><MessageSquare className="h-4 w-4 mr-1.5" />Collaboration</TabsTrigger>
          <TabsTrigger value="tax" data-testid="const-shell-tax"><Landmark className="h-4 w-4 mr-1.5" />Tax &amp; Policies</TabsTrigger>
          <TabsTrigger value="rates" data-testid="const-shell-rates"><Package className="h-4 w-4 mr-1.5" />Rate Sheets</TabsTrigger>
          <TabsTrigger value="forex" data-testid="const-shell-forex"><Coins className="h-4 w-4 mr-1.5" />Forex</TabsTrigger>
        </TabsList>

        <TabsContent value="calc">
          <ConstructionCalculator onCalculated={handleCalculated} result={calculation} onSaved={(id) => setSavedProjectId(id)} />
        </TabsContent>
        <TabsContent value="resources"><ResourcesPlan calculation={calculation} /></TabsContent>
        <TabsContent value="risk"><RiskAndChange calculation={calculation} onEvaluation={setRiskEval} /></TabsContent>
        <TabsContent value="funding"><FundingDrawdown calculation={calculation} riskAdjustedCost={riskAdjustedCost} revisedBurn={revisedBurn} /></TabsContent>
        <TabsContent value="evm"><EvmAnalysis calculation={calculation} /></TabsContent>
        <TabsContent value="kpi"><KpiDashboard calculation={calculation} riskEval={riskEval} /></TabsContent>
        <TabsContent value="dashboard"><Dashboard calculation={calculation} riskEval={riskEval} /></TabsContent>
        <TabsContent value="bla"><BaselineActuals
          calculation={calculation}
          savedProjectId={savedProjectId}
          onSaveRequest={() => setTab('calc')}
          onRevisedBurn={(rb) => { setRevisedBurn(rb); setTab('funding'); }}
        /></TabsContent>
        <TabsContent value="collab"><Collaboration calculation={calculation} savedProjectId={savedProjectId} onSaveRequest={() => setTab('calc')} /></TabsContent>
        <TabsContent value="tax"><TaxPolicies region={calculation?.region} /></TabsContent>
        <TabsContent value="rates"><RateSheetView region={calculation?.region} /></TabsContent>
        <TabsContent value="forex"><ForexView /></TabsContent>
      </Tabs>
    </div>
  );
}
